import "./Messages.css"
import { length, map, merge } from "@pdfking/rfunc"
import { Items, Range, Values } from "@pdfking/tfunc"
import tracking from "@pdfking/workflow/state/tracking"
import make_translations from "@pdfking/workflow/utils/make_translations"
import proxy from "@pdfking/workflow/utils/proxy"
import { defineComponent, TransitionGroup } from "vue"
import messages from "../state/messages"

const calculate_series = (
  l: number,
  f: (v: number, i: number) => number,
  s: number,
  formatter: ((v: number) => string) | null = null
) => {
  if (l == 0) {
    return []
  }

  const result = new Array(l)
  result[l - 1] = s || 1

  for (let i = l - 2; i >= 0; i--) {
    result[i] = f(result[i + 1], i)
  }

  if (formatter) {
    return map(formatter, result)
  }

  return result
}

export default defineComponent({
  computed: merge(
    proxy(messages, { messages: "messages", remove: "message_remove" }),
    proxy(tracking, ["is_loading", "trackers"]),
    {
      message_length(): number {
        // @ts-expect-error - Vue proxy prop
        return length(this.messages)
      },

      message_scale() {
        // @ts-expect-error - Vue data prop
        if (this.is_hover) {
          return calculate_series(
            this.message_length as unknown as number,
            (v: number) => v / 1.02,
            1
          )
        }

        return calculate_series(
          this.message_length as unknown as number,
          (v: number) => v / 1.05,
          1
        )
      },
      message_translate() {
        // @ts-expect-error - Vue data prop
        if (this.is_hover) {
          const len = this.message_length as unknown as number
          return map(
            (i: number) =>
              `calc((${(len - i - 1) * 100}% - ${(len - i - 1) * 8}px) * -1)`,
            Range(this.message_length as unknown as number)
          )
        }
        return calculate_series(
          this.message_length as unknown as number,
          (v: number) => v - 8,
          0,
          (v: number) => `${v}px`
        )
      },
      translations: () => make_translations("actions"),
    }
  ),
  data: () => ({ is_hover: false }),
  methods: {
    length,
    toggle_hover(hover: boolean) {
      this.is_hover = hover
    },
    Values,
  },
  props: [] as const,
  render() {
    return (
      <div class="position-fixed bottom-0 w-100 pe-none" style="z-index: 3000">
        <div class="row g-0 justify-content-center">
          <div
            class="col-12 col-lg-6 position-relative"
            onMouseleave={() => this.toggle_hover(false)}
            onMouseover={() => this.toggle_hover(true)}
          >
            <TransitionGroup name="message">
              {{
                default: () =>
                  Values(this.messages).map((m: any, i: number) => (
                    <div
                      key={m.id}
                      class={[
                        "alert clickable m-0 mb-4 shadow text-center position-absolute bottom-0 w-100 pe-auto d-flex justify-content-center align-items-center lead",
                        `alert-${m.class || "success"}`,
                      ]}
                      style={{
                        transform: `scale(${this.message_scale[i]}, ${this.message_scale[i]}) translate(0, ${this.message_translate[i]})`,
                      }}
                      onClick={() => this.message_remove(m.id)}
                    >
                      <span innerHTML={m.content} />
                      {m.href && (
                        <a
                          class="stretched-link"
                          href={m.href}
                          target="_blank"
                        />
                      )}
                    </div>
                  )),
              }}
            </TransitionGroup>
          </div>

          <div
            class={[
              "col-12 col-lg-7 position-relative progress-tracking",
              { "progress-loading": this.is_loading },
            ]}
          >
            <div class="d-flex shadow mb-0 py-2 px-1 bg-white position-absolute w-100 bottom-0 rounded-top border">
              <TransitionGroup name="tracker">
                {{
                  default: () => {
                    if (length(this.trackers) == 0) {
                      return <div key="empty" class="progress w-100 mx-1" />
                    }
                    return Values(this.trackers).map(
                      (tracker: any, i: number) => (
                        <div
                          key={i}
                          class="progress text-start bg-light position-relative progress-bar-striped progress-bar-animated mx-1"
                          style={{ width: `${tracker.width * 100}%` }}
                        >
                          {Items(tracker.status).map(
                            ([statusKey, status]: [string, any]) => (
                              <div
                                key={statusKey}
                                class={[
                                  "progress-bar",
                                  {
                                    "bg-danger": statusKey == "danger",
                                    "progress-bar-striped progress-bar-animated":
                                      statusKey == "loading",
                                  },
                                ]}
                                style={{ width: `${status.width * 100}%` }}
                              ></div>
                            )
                          )}
                          <div
                            class="position-absolute ms-2 text-white"
                            style="mix-blend-mode: difference"
                          >
                            {tracker.title}
                            {tracker.aggregated.done < tracker.aggregated.total
                              ? "…"
                              : null}
                            {tracker.aggregated.done >=
                              tracker.aggregated.total && (
                              <span class="text-success">✓</span>
                            )}
                          </div>
                        </div>
                      )
                    )
                  },
                }}
              </TransitionGroup>
            </div>
          </div>
        </div>
      </div>
    )
  },
})
